/**
 * TransferActions Component
 *
 * Conditional action bar for the transfer drawer.
 * @see Sprint 14 - Transferts Inter-Sites
 * @see Sprint 22 - Transferts par Packaging
 */

import { Button } from '@/components/ui';
import {
  Package,
  Check,
  X,
  Truck,
  Pencil,
  Send,
} from 'lucide-react';
import type { DrawerMode } from './useTransferDrawerState';

interface TransferActionsProps {
  mode: DrawerMode;
  // Permissions
  canSubmit: boolean;
  canShip: boolean;
  canReceive: boolean;
  canCancel: boolean;
  canEdit: boolean;
  // Edit mode
  editLinesCount: number;
  onSaveEdit: () => void;
  onCancelEditing: () => void;
  isUpdatePending: boolean;
  // Ship mode
  onShip: () => void;
  onCancelShipping: () => void;
  isShipPending: boolean;
  // Receive mode
  onReceive: () => void;
  onCancelReceiving: () => void;
  isReceivePending: boolean;
  // View mode actions
  onStartCancel: () => void;
  onStartEditing: () => void;
  onSubmit: () => void;
  isSubmitPending: boolean;
  onStartShipping: () => void;
  onStartReceiving: () => void;
}

export function TransferActions({
  mode,
  canSubmit,
  canShip,
  canReceive,
  canCancel,
  canEdit,
  editLinesCount,
  onSaveEdit,
  onCancelEditing,
  isUpdatePending,
  onShip,
  onCancelShipping,
  isShipPending,
  onReceive,
  onCancelReceiving,
  isReceivePending,
  onStartCancel,
  onStartEditing,
  onSubmit,
  isSubmitPending,
  onStartShipping,
  onStartReceiving,
}: TransferActionsProps) {
  if (mode === 'cancel') return null;

  return (
    <div className="border-t border-neutral-200 pt-6 flex gap-3 justify-end">
      {mode === 'edit' ? (
        /* Edit mode actions */
        <>
          <Button variant="secondary" onClick={onCancelEditing}>
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={onSaveEdit}
            disabled={editLinesCount === 0 || isUpdatePending}
          >
            <Check className="w-4 h-4 mr-2" />
            Enregistrer
          </Button>
        </>
      ) : mode === 'ship' ? (
        /* Shipping mode actions */
        <>
          <Button variant="secondary" onClick={onCancelShipping}>
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={onShip}
            disabled={isShipPending}
          >
            <Truck className="w-4 h-4 mr-2" />
            Confirmer l'expedition
          </Button>
        </>
      ) : mode === 'receive' ? (
        /* Receiving mode actions */
        <>
          <Button variant="secondary" onClick={onCancelReceiving}>
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={onReceive}
            disabled={isReceivePending}
          >
            <Check className="w-4 h-4 mr-2" />
            Valider la reception
          </Button>
        </>
      ) : (
        /* Normal mode actions */
        <>
          {canCancel && (
            <Button variant="danger" onClick={onStartCancel}>
              <X className="w-4 h-4 mr-2" />
              Annuler
            </Button>
          )}
          {canEdit && (
            <Button variant="secondary" onClick={onStartEditing}>
              <Pencil className="w-4 h-4 mr-2" />
              Modifier
            </Button>
          )}
          {canSubmit && (
            <Button
              variant="primary"
              onClick={onSubmit}
              disabled={isSubmitPending}
            >
              <Send className="w-4 h-4 mr-2" />
              Soumettre
            </Button>
          )}
          {canShip && (
            <Button
              variant="primary"
              onClick={onStartShipping}
              disabled={isShipPending}
            >
              <Truck className="w-4 h-4 mr-2" />
              Expedier
            </Button>
          )}
          {canReceive && (
            <Button variant="primary" onClick={onStartReceiving}>
              <Package className="w-4 h-4 mr-2" />
              Receptionner
            </Button>
          )}
        </>
      )}
    </div>
  );
}
